import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, MessageSquare, Briefcase, ClipboardCheck, Calendar, ArrowRight, CheckCircle2, Circle, AlertTriangle, FileCheck } from 'lucide-react';
export function DashboardPage() {
  const quickAccess = [{
    title: 'My Courses',
    icon: BookOpen,
    path: '/courses',
    desc: 'Continue your learning journey'
  }, {
    title: 'My Advisor',
    icon: Briefcase,
    path: '/advisory',
    desc: 'Connect with Dr. Marcus Chen'
  }, {
    title: 'Community Hub',
    icon: Users,
    path: '/community',
    desc: 'Engage with Cohort 1'
  }, {
    title: 'Needs Assessment',
    icon: ClipboardCheck,
    path: '/assessment',
    desc: 'Update your business profile'
  }, {
    title: 'Events Calendar',
    icon: Calendar,
    path: '/calendar',
    desc: 'Upcoming workshops & sessions'
  }, {
    title: 'Chat',
    icon: MessageSquare,
    path: '/chat',
    desc: 'Real-time discussions'
  }];
  const checklist = [{
    title: 'Complete Profile Setup',
    completed: true
  }, {
    title: 'Initial Business Diagnostics',
    completed: true
  }, {
    title: 'Schedule First Advisor Meeting',
    completed: true
  }, {
    title: 'Upload Financial Projections',
    completed: false
  }];
  return <div className="space-y-8 animate-in fade-in duration-500" data-id="element-464">
      {/* Grant Eligibility Alert */}
      <Link to="/survey" className="block bg-catalyst-mustard/10 border border-catalyst-mustard/30 rounded-xl p-5 hover:bg-catalyst-mustard/15 transition-colors group" data-id="element-465">
        <div className="flex items-start gap-4" data-id="element-466">
          <div className="p-2.5 rounded-lg bg-catalyst-mustard/20 shrink-0" data-id="element-467">
            <AlertTriangle size={22} className="text-catalyst-mustard-dark" data-id="element-468" />
          </div>
          <div className="flex-1" data-id="element-469">
            <div className="flex items-center gap-2 mb-1" data-id="element-470">
              <h3 className="font-bold text-catalyst-black" data-id="element-471">
                Mandatory Survey Incomplete
              </h3>
              <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full border border-red-100 uppercase tracking-wider" data-id="element-472">
                Action Required
              </span>
            </div>
            <p className="text-sm text-catalyst-brown-dark" data-id="element-473">
              You must complete the Mid-Program Progress Questionnaire to remain
              eligible for your end-of-program grant. Incomplete surveys will
              result in grant withholding.
            </p>
          </div>
          <div className="flex items-center gap-2 text-catalyst-mustard-dark font-semibold text-sm shrink-0 group-hover:gap-3 transition-all" data-id="element-474">
            <FileCheck size={18} data-id="element-475" />
            Complete Now
            <ArrowRight size={16} data-id="element-476" />
          </div>
        </div>
      </Link>

      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-477">
        <div data-id="element-478">
          <h1 className="text-3xl font-heading font-bold text-catalyst-black tracking-tight" data-id="element-479">
            Welcome back, Sarah
          </h1>
          <p className="text-gray-500 mt-1" data-id="element-480">
            Friday, March 20, 2026 • Cohort 1
          </p>
        </div>
        <button className="bg-catalyst-black text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-catalyst-brown-dark transition-colors shadow-sm" data-id="element-481">
          View Program Guide
        </button>
      </div>

      {/* Progress Tracker */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100" data-id="element-482">
        <div className="flex justify-between items-end mb-4" data-id="element-483">
          <div data-id="element-484">
            <h2 className="text-lg font-semibold text-catalyst-black" data-id="element-485">
              Program Progress
            </h2>
            <p className="text-sm text-gray-500" data-id="element-486">
              Month 3 of 6 • Financial Modeling
            </p>
          </div>
          <span className="text-2xl font-bold text-catalyst-mustard-dark" data-id="element-487">
            50%
          </span>
        </div>

        <div className="relative pt-2" data-id="element-488">
          <div className="overflow-hidden h-2 mb-4 text-xs flex rounded-full bg-gray-100" data-id="element-489">
            <div style={{
            width: '50%'
          }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-catalyst-mustard transition-all duration-1000 ease-out" data-id="element-490"></div>
          </div>
          <div className="flex justify-between text-xs text-gray-400 font-medium px-1" data-id="element-491">
            <span data-id="element-492">M1</span>
            <span data-id="element-493">M2</span>
            <span className="text-catalyst-mustard-dark font-bold" data-id="element-494">M3</span>
            <span data-id="element-495">M4</span>
            <span data-id="element-496">M5</span>
            <span data-id="element-497">M6</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-498">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-8" data-id="element-499">
          {/* Announcements */}
          <section data-id="element-500">
            <div className="flex items-center justify-between mb-4" data-id="element-501">
              <h2 className="text-xl font-semibold text-catalyst-black" data-id="element-502">
                Latest Updates
              </h2>
              <Link to="/community" className="text-sm text-catalyst-mustard-dark hover:text-catalyst-mustard font-medium flex items-center gap-1 transition-colors" data-id="element-503">
                View all <ArrowRight size={16} data-id="element-504" />
              </Link>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-505">
              <div className="p-5 border-l-4 border-catalyst-mustard bg-catalyst-off-white" data-id="element-506">
                <div className="flex justify-between items-start mb-2" data-id="element-507">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-catalyst-mustard/20 text-catalyst-brown-dark" data-id="element-508">
                    Important
                  </span>
                  <span className="text-xs text-gray-400" data-id="element-509">2 hours ago</span>
                </div>
                <h3 className="text-base font-semibold text-catalyst-black mb-1" data-id="element-510">
                  Q1 Financial Review Templates Available
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2" data-id="element-511">
                  The updated financial modeling templates for Month 3 are now
                  available in the resources section. Please review these before
                  your next advisory session.
                </p>
              </div>
            </div>
          </section>

          {/* Quick Access Grid */}
          <section data-id="element-512">
            <h2 className="text-xl font-semibold text-catalyst-black mb-4" data-id="element-513">
              Quick Access
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-514">
              {quickAccess.map((item, idx) => {
              const Icon = item.icon;
              return <Link key={idx} to={item.path} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 hover:border-catalyst-mustard/50 hover:shadow-md transition-all group flex items-start gap-4" data-id="element-515">
                    <div className="p-3 rounded-lg bg-catalyst-cream text-catalyst-brown-dark group-hover:bg-catalyst-mustard/10 group-hover:text-catalyst-mustard-dark transition-colors" data-id="element-516">
                      <Icon size={24} data-id="element-517" />
                    </div>
                    <div data-id="element-518">
                      <h3 className="font-semibold text-catalyst-black group-hover:text-catalyst-mustard-dark transition-colors" data-id="element-519">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-520">{item.desc}</p>
                    </div>
                  </Link>;
            })}
            </div>
          </section>
        </div>

        {/* Right Sidebar Area */}
        <div className="space-y-6" data-id="element-521">
          {/* Next Milestone */}
          <div className="bg-catalyst-black text-white rounded-xl shadow-sm p-6 relative overflow-hidden" data-id="element-522">
            <div className="absolute top-0 right-0 w-32 h-32 bg-catalyst-mustard/10 rounded-full blur-2xl -mr-10 -mt-10" data-id="element-523"></div>
            <h2 className="text-sm font-medium text-gray-400 mb-1 uppercase tracking-wider" data-id="element-524">
              Next Milestone
            </h2>
            <h3 className="text-xl font-bold mb-4" data-id="element-525">Pitch Deck Review</h3>

            <div className="flex items-center gap-3 mb-6 bg-white/10 rounded-lg p-3" data-id="element-526">
              <Calendar className="text-catalyst-mustard" size={20} data-id="element-527" />
              <div data-id="element-528">
                <p className="text-sm font-medium" data-id="element-529">March 25, 2026</p>
                <p className="text-xs text-gray-400" data-id="element-530">2:00 PM EST</p>
              </div>
            </div>

            <button className="w-full bg-catalyst-mustard text-catalyst-black py-2.5 rounded-lg text-sm font-semibold hover:bg-catalyst-mustard-light transition-colors shadow-sm" data-id="element-531">
              Prepare Materials
            </button>
          </div>

          {/* Assigned Advisor */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-532">
            <h2 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wider" data-id="element-533">
              Your Lead Advisor
            </h2>
            <div className="flex items-center gap-4 mb-5" data-id="element-534">
              <div className="w-14 h-14 rounded-full bg-catalyst-brown-light flex items-center justify-center text-white font-bold text-xl shadow-inner" data-id="element-535">
                MC
              </div>
              <div data-id="element-536">
                <h3 className="font-semibold text-catalyst-black" data-id="element-537">
                  Dr. Marcus Chen
                </h3>
                <p className="text-xs text-gray-500" data-id="element-538">
                  Growth Strategy & Fundraising
                </p>
              </div>
            </div>
            <Link to="/advisory" className="block w-full text-center border border-gray-200 text-catalyst-black py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors" data-id="element-539">
              Book Session
            </Link>
          </div>

          {/* Onboarding Checklist */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-id="element-540">
            <div className="flex justify-between items-center mb-4" data-id="element-541">
              <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-542">
                Onboarding
              </h2>
              <span className="text-xs font-bold text-catalyst-mustard-dark bg-catalyst-mustard/10 px-2 py-1 rounded-full" data-id="element-543">
                3/4 Done
              </span>
            </div>

            <ul className="space-y-3" data-id="element-544">
              {checklist.map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-545">
                  {item.completed ? <CheckCircle2 className="text-green-500 mt-0.5 shrink-0" size={18} data-id="element-546" /> : <Circle className="text-gray-300 mt-0.5 shrink-0" size={18} data-id="element-547" />}
                  <span className={`text-sm ${item.completed ? 'text-gray-500 line-through decoration-gray-300' : 'text-catalyst-black font-medium'}`} data-id="element-548">
                    {item.title}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}